import React from 'react';

const StatsCircularCard = ({ statsDetail, language, categoryLabel }) => {
    return (
        <div className="bg-white dark:bg-[#121212] rounded-2xl md:rounded-[2rem] p-4 md:p-10 border border-gray-200 dark:border-white/5 flex flex-col items-center justify-center relative overflow-hidden shadow-sm dark:shadow-none">
            <h3 className="w-full text-left font-medium text-xs text-gray-500 dark:text-gray-500 flex items-center gap-2 mb-6 md:mb-12">
                <svg className="w-4 h-4 text-gray-400 dark:text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                </svg>
                {categoryLabel || (language === 'fr' ? 'Moyenne générale' : 'General Average')}
            </h3>

            {/* Circular Progress */}
            <div className="relative w-40 h-40 md:w-64 md:h-64 flex items-center justify-center mb-6 md:mb-10">
                {/* Main Outer Circle */}
                <div className="absolute inset-0 rounded-full border-[10px] border-gray-100 dark:border-white/5"></div>

                <svg className="w-full h-full transform -rotate-90 drop-shadow-[0_0_15px_rgba(52,211,153,0.2)]" viewBox="0 0 256 256">
                    <circle
                        cx="128"
                        cy="128"
                        r="110"
                        stroke="url(#progressGradient)"
                        strokeWidth="12"
                        fill="transparent"
                        strokeDasharray="691"
                        strokeDashoffset={691 - (691 * (statsDetail?.averageScore || 0)) / 100}
                        className="transition-all duration-1000 ease-out"
                        style={{ strokeLinecap: 'round' }}
                    />
                    <defs>
                        <linearGradient id="progressGradient" x1="0%" y1="0%" x2="100%" y2="0%">
                            <stop offset="0%" stopColor="#34d399" />
                            <stop offset="100%" stopColor="#10b981" />
                        </linearGradient>
                    </defs>

                    {/* Tick marks effect overlay - matches bg color to create gaps */}
                    <circle
                        cx="128"
                        cy="128"
                        r="110"
                        stroke="currentColor"
                        strokeWidth="14"
                        fill="transparent"
                        strokeDasharray="2 8"
                        className="text-white dark:text-[#121212]"
                        style={{ strokeLinecap: 'butt' }}
                    />
                </svg>

                <div className="absolute flex flex-col items-center group/avg cursor-default">
                    <div className="flex items-baseline gap-1">
                        <span className="text-4xl md:text-7xl font-black text-gray-900 dark:text-white leading-none">{statsDetail?.averageScore || 0}</span>
                        <span className="text-lg md:text-2xl font-black text-gray-300 dark:text-white/30">%</span>
                    </div>
                    <span className="text-[10px] md:text-xs font-bold text-gray-400 dark:text-gray-500 mt-1 md:mt-2 uppercase tracking-wider">
                        {language === 'fr' ? 'Moyenne globale' : 'Overall Average'}
                    </span>
                    {/* Tooltip */}
                    <div className="absolute top-full mt-4 z-50 w-56 opacity-0 scale-95 pointer-events-none group-hover/avg:opacity-100 group-hover/avg:scale-100 transition-all duration-200 origin-top">
                        <div className="bg-gray-900 dark:bg-gray-800 rounded-xl p-3 shadow-2xl border border-white/10">
                            <p className="text-[10px] md:text-[11px] text-gray-300 leading-relaxed text-center">
                                {language === 'fr'
                                    ? 'Moyenne calculée sur la base de toutes les sessions que tu as validées pour ce module.'
                                    : 'Average calculated from all sessions you have completed for this module.'}
                            </p>
                        </div>
                        {/* Arrow */}
                        <div className="absolute -top-1 left-1/2 -translate-x-1/2 w-2 h-2 bg-gray-900 dark:bg-gray-800 rotate-45 border-l border-t border-white/10" />
                    </div>
                </div>
            </div>

            {/* Footer Stats */}
            <div className="w-full grid grid-cols-3 divide-x divide-gray-100 dark:divide-white/5 pt-4 md:pt-8 border-t border-gray-100 dark:border-white/5 mt-6 md:mt-0">
                <div className="text-center px-1 md:px-2">
                    <div className="text-lg md:text-xl font-black text-gray-900 dark:text-white">{statsDetail?.highestScore || 0}<span className="text-xs ml-0.5 opacity-40"> %</span></div>
                    <div className="text-[8px] md:text-[10px] font-medium text-gray-400 dark:text-gray-500 mt-0.5 md:mt-1 leading-tight whitespace-pre-line">
                        {language === 'fr' ? 'Score\nle plus haut' : 'Highest\nScore'}
                    </div>
                </div>
                <div className="text-center px-1 md:px-2">
                    <div className="text-lg md:text-xl font-black text-gray-900 dark:text-white">{statsDetail?.lowestScore || 0}<span className="text-xs ml-0.5 opacity-40"> %</span></div>
                    <div className="text-[8px] md:text-[10px] font-medium text-gray-400 dark:text-gray-500 mt-0.5 md:mt-1 leading-tight whitespace-pre-line">
                        {language === 'fr' ? 'Score\nle plus bas' : 'Lowest\nScore'}
                    </div>
                </div>
                <div className="text-center px-1 md:px-2">
                    <div className="text-lg md:text-xl font-black text-gray-900 dark:text-white">{statsDetail?.totalTime || '0h 00'}</div>
                    <div className="text-[8px] md:text-[10px] font-medium text-gray-400 dark:text-gray-500 mt-0.5 md:mt-1 leading-tight whitespace-pre-line">
                        {language === 'fr' ? 'Temps total\npassé' : 'Total time\nspent'}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default StatsCircularCard;
